import type { LicenseState } from "@clubcore/api";
import { useMemo, useSyncExternalStore, type ReactNode } from "react";
import { useT } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/keys";
import { WritableContext } from "../ui/writable";
import { isWritable } from "./license";
import { useServices } from "./services";

export function licenseStateKey(state: LicenseState): MessageKey {
  return `license.state.${state}` as MessageKey;
}

/** The license as this browser has learned it, and what it means for the screens. */
export function useLicense() {
  const { license } = useServices();
  const snapshot = useSyncExternalStore(license.subscribe, license.getSnapshot);
  const status = snapshot.status;
  return {
    status,
    readOnly: !isWritable(status),
    inGrace: status === "grace",
    blockedModules: snapshot.blockedModules,
  };
}

/** Tells every `write` button whether the installation is read-only. */
export function LicenseProvider({ children }: { children: ReactNode }) {
  const t = useT();
  const { status, readOnly } = useLicense();
  const value = useMemo(
    () => ({
      readOnly,
      reason: readOnly && status ? t("license.readOnlyShort", { state: t(licenseStateKey(status)).toLowerCase() }) : undefined,
    }),
    [readOnly, status, t],
  );
  return <WritableContext.Provider value={value}>{children}</WritableContext.Provider>;
}
